import { useNavigate } from '@share/utils';
import { useLocation } from 'react-router-dom';
import { Modal, Button, Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import { Routes } from '@share/constants';

import calendarImage from '@assets/images/calendar.png';
import './style.scss';

const { Title, Text } = Typography;

export interface OfferExpiredModalProps {
  visible: boolean;
  accountName: string;

  /** When true: button "Back to search" and navigate to search. When false/omitted: button "Back to activity details" and navigate to detail. */
  backToSearch?: boolean;

  activityCode?: string;

  /** Called when user clicks "Back to search" (only when backToSearch is true). Use e.g. to dispatch a search refresh before navigating. */
  onBackToSearch?: () => void;
}

export function OfferExpiredModal({
  visible,
  backToSearch = false,
  accountName,
  activityCode = '',
  onBackToSearch,
}: OfferExpiredModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchSuffix = (location.search || '').replace(/^\?\?/, '?');
  const detailUrl = `/${accountName}${Routes.ActivityDetail}/${activityCode}${searchSuffix}`;
  const searchUrl = `/${accountName}${Routes.ActivitiesSearch}${searchSuffix}`;
  const backUrl = backToSearch ? searchUrl : detailUrl;

  const handleBack = () => {
    if (backToSearch) {
      onBackToSearch?.();
    }
    navigate(backUrl);
  };

  return (
    <Modal
      visible={visible}
      closable={false}
      footer={null}
      width={400}
      className="offer-expired-modal"
      mask
    >
      <div className="offer-expired-modal__content">
        <img src={calendarImage} alt="" className="offer-expired-modal__image" width={100} />
        <Title level={4} className="offer-expired-modal__title">
          <FormattedMessage id="checkout.offerExpiredTitle" defaultMessage="Offer expired" />
        </Title>
        <Text className="offer-expired-modal__message">
          {backToSearch ? (
            <FormattedMessage
              id="checkout.offerExpiredMessageSearch"
              defaultMessage="This offer has expired. Please return to the search to find available activities."
            />
          ) : (
            <FormattedMessage
              id="checkout.offerExpiredMessage"
              defaultMessage="This offer has expired. Please return to the activity details to check availability again."
            />
          )}
        </Text>
        <Button type="primary" className="offer-expired-modal__button" onClick={handleBack}>
          {backToSearch ? (
            <FormattedMessage id="details.backToSearch" defaultMessage="Back to search" />
          ) : (
            <FormattedMessage id="checkout.backToActivityDetails" defaultMessage="Back to activity details" />
          )}
        </Button>
      </div>
    </Modal>
  );
}
